import type { MeetingSummary, SummaryTranslation, SummaryVersion } from "@scoach/types";
import type { Meeting } from "@scoach/types";
import { LANGUAGES, getScenarioById, langMeta } from "@scoach/types";
import { useToast } from "@scoach/ui";
import {
  Bolt,
  Brain,
  Check,
  Chev,
  Copy,
  Doc,
  Notebook,
  Send,
  Share,
  Spark,
} from "@scoach/ui/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { FeedbackStars } from "../../../components/FeedbackStars.tsx";
import { MeetingLabels } from "../../../components/MeetingLabels.tsx";
import { driveApi } from "../../../lib/driveApi.ts";
import { reportClientError } from "../../../lib/errorReporter.ts";
import { getDriveAuthCode } from "../../../lib/googleToken.ts";
import { ApiError, api } from "../../../lib/http.ts";
import { useAuthStore } from "../../auth/store.ts";
import { SuggestedQuestionsBanner } from "../../settings/components/SuggestedQuestionsBanner.tsx";
import { summaryApi } from "../api.ts";
import { ClientEmail } from "./ClientEmail.tsx";
import { FullTranscript } from "./FullTranscript.tsx";
import { InternalSummary } from "./InternalSummary.tsx";
import { ShareModal } from "./ShareModal.tsx";
import { SummaryPluginsPanel } from "./SummaryPluginsPanel.tsx";
import { TranscriptChat } from "./TranscriptChat.tsx";

type Tab = "internal" | "client" | "transcript" | "plugins";

/** Overlay `tr[i]` over each `orig[i]` (keeping originals where the translation is
 * missing / shorter), so array-length drift from the model never loses content. */
function overlay(orig: string[] | undefined, tr: string[] | undefined): string[] | undefined {
  if (!tr) return orig;
  if (!orig) return tr;
  return orig.map((o, i) => tr[i] ?? o);
}

/** Build a translated view of a summary by overlaying a SummaryTranslation over the
 * English original (used for read-only "view in the meeting language"). */
function applyTranslation(s: MeetingSummary, t: SummaryTranslation): MeetingSummary {
  const i = s.internal;
  const c = s.client;
  return {
    ...s,
    internal: {
      ...i,
      overview: i.overview
        ? {
            ...i.overview,
            summary: t.overviewSummary ?? i.overview.summary,
            whatWasDone: overlay(i.overview.whatWasDone, t.whatWasDone) ?? i.overview.whatWasDone,
          }
        : i.overview,
      wentWell: overlay(i.wentWell, t.wentWell) ?? i.wentWell,
      couldImprove: overlay(i.couldImprove, t.couldImprove) ?? i.couldImprove,
      risks: overlay(i.risks, t.risks) ?? i.risks,
      needs: {
        stated: overlay(i.needs?.stated, t.needsStated) ?? i.needs?.stated ?? [],
        actual: overlay(i.needs?.actual, t.needsActual) ?? i.needs?.actual ?? [],
      },
      actionItems: (i.actionItems ?? []).map((a, idx) => ({
        ...a,
        what: t.actionItems?.[idx] ?? a.what,
      })),
      topMoments: (i.topMoments ?? []).map((m, idx) => ({
        ...m,
        quote: t.topMoments?.[idx] ?? m.quote,
      })),
      questionsAsked: (i.questionsAsked ?? []).map((q, idx) => ({
        ...q,
        q: t.questionsAsked?.[idx] ?? q.q,
      })),
      customSummary: (i.customSummary ?? []).map((x, idx) => ({
        ...x,
        answer: t.customAnswers?.[idx] ?? x.answer,
      })),
      upsell: (i.upsell ?? []).map((u, idx) => ({
        ...u,
        name: t.upsell?.[idx]?.name ?? u.name,
        reason: t.upsell?.[idx]?.reason ?? u.reason,
      })),
    },
    client: {
      ...c,
      subject: t.email?.subject ?? c.subject,
      greeting: t.email?.greeting ?? c.greeting,
      body: t.email?.body ?? c.body,
      signoff: t.email?.signoff ?? c.signoff,
      // Force the assembled greeting/body/signoff to render (ignore any edited English bodyText).
      bodyText: undefined,
    },
  };
}

export interface SummaryScreenProps {
  meetingId: string;
}

export function SummaryScreen({ meetingId }: SummaryScreenProps) {
  const nav = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("internal");
  const [summary, setSummary] = useState<MeetingSummary | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  // Last successful Drive export (url + file name), shown as a persistent link next to
  // the button so the user can open the created file even after the toast disappears.
  const [driveExport, setDriveExport] = useState<{ url: string; fileName: string } | null>(null);
  // "View in the meeting language" — translate on demand + cache; read-only view.
  const [displayLang, setDisplayLang] = useState<string>("en");
  const [translation, setTranslation] = useState<SummaryTranslation | null>(null);
  const [translating, setTranslating] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [versions, setVersions] = useState<Omit<SummaryVersion, "summary">[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  // Read-only preview of a previous version: we load its payload into view without
  // overwriting `latest` or re-analyzing. `latestBackup` holds the live summary so
  // "Back to latest" restores the view instantly.
  const [previewVersion, setPreviewVersion] = useState<{ id: string; label: string } | null>(null);
  const [latestBackup, setLatestBackup] = useState<MeetingSummary | null>(null);
  const [switchingVersion, setSwitchingVersion] = useState(false);

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Canceller for the currently-running summary auto-retry loop, so starting a
  // new one (manual "Retry now") tears down the previous loop instead of stacking.
  const loadCancelRef = useRef<(() => void) | null>(null);

  // Quick-meeting naming: when the meeting has an auto-generated title, prompt
  // the user with a suggested name (from the shared screen / conversation) while
  // the summary is still generating.
  const [namingOpen, setNamingOpen] = useState(false);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [nameInput, setNameInput] = useState("");
  const [companyInput, setCompanyInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  // Voice-of-the-Field consent (default true / opt-out). Persisted immediately on toggle.
  const [vofConsent, setVofConsent] = useState(true);

  // When the meeting was started without a company (quick "Skip"), also collect it.
  const COMPANY_PLACEHOLDERS = new Set([
    "",
    "untitled",
    "quick meeting",
    "new meeting",
    "meeting",
    "unknown",
  ]);
  const needCompany = COMPANY_PLACEHOLDERS.has((meeting?.account?.name ?? "").trim().toLowerCase());

  useEffect(() => {
    api<Meeting>(`/meetings/${meetingId}`)
      .then(setMeeting)
      .catch(() => {});
  }, [meetingId]);

  // Seed the VoF consent from the loaded meeting (default true when unset).
  useEffect(() => {
    setVofConsent(meeting?.voiceOfFieldConsent !== false);
  }, [meeting?.voiceOfFieldConsent]);

  function toggleVofConsent(next: boolean) {
    setVofConsent(next);
    setMeeting((m) => (m ? { ...m, voiceOfFieldConsent: next } : m));
    void api(`/meetings/${meetingId}`, {
      method: "PATCH",
      body: { voiceOfFieldConsent: next },
    }).catch(() => {});
  }

  // Only the meeting owner can edit labels (the server PATCH is owner-only; shared
  // viewers / admins see them read-only). Unknown owner (older meetings) => editable.
  const currentUid = useAuthStore((s) => s.user?.uid ?? s.firebaseUid);
  const canEditLabels =
    !!meeting && (!meeting.ownerUid || !currentUid || meeting.ownerUid === currentUid);

  // Save labels: optimistic local update + owner PATCH (server sanitizes/dedupes).
  function saveLabels(next: string[]) {
    setMeeting((m) => (m ? { ...m, labels: next } : m));
    void summaryApi.updateLabels(meetingId, next).catch(() => {});
  }

  // Remember the last language the summary was viewed in, per meeting, so reopening it
  // shows it in that language automatically. Best-effort (owner-only PATCH); the server
  // already caches the translation itself, so re-selection is instant regardless.
  function persistSummaryLang(lang: string) {
    if ((meeting?.summaryLang ?? "en") === lang) return;
    setMeeting((m) => (m ? { ...m, summaryLang: lang as Meeting["summaryLang"] } : m));
    void api(`/meetings/${meetingId}`, {
      method: "PATCH",
      body: { summaryLang: lang },
    }).catch(() => {});
  }

  async function viewInLang(lang: string) {
    if (lang === "en") {
      setDisplayLang("en");
      persistSummaryLang("en");
      return;
    }
    if (translation?.lang === lang) {
      setDisplayLang(lang);
      persistSummaryLang(lang);
      return;
    }
    setTranslating(true);
    try {
      const t = await summaryApi.translate(meetingId, lang);
      setTranslation(t);
      setDisplayLang(lang);
      persistSummaryLang(lang);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setTranslating(false);
    }
  }

  // Auto-apply the meeting's remembered view language once, after both the summary and
  // meeting have loaded, so reopening a summary shows it in the last-chosen language.
  const autoLangAppliedRef = useRef(false);
  useEffect(() => {
    if (autoLangAppliedRef.current) return;
    if (!summary || !meeting) return;
    autoLangAppliedRef.current = true;
    const lang = meeting.summaryLang;
    if (lang && lang !== "en") void viewInLang(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, meeting]);

  useEffect(() => {
    if (!meeting?.autoTitled) return;
    // Only nudge the meeting's OWNER to name it — admins / shared viewers opening
    // someone else's meeting shouldn't be prompted to rename it.
    const uid = useAuthStore.getState().user?.uid ?? useAuthStore.getState().firebaseUid;
    if (meeting.ownerUid && uid && meeting.ownerUid !== uid) return;
    setNamingOpen(true);
    setSuggested(null);
    setSuggestions([]);
    let cancelled = false;
    summaryApi
      .suggestTitle(meetingId)
      .then((r) => {
        if (cancelled) return;
        const list = (r.suggestions ?? (r.suggestedTitle ? [r.suggestedTitle] : []))
          .map((t) => t.trim())
          .filter(Boolean);
        const first = list[0] ?? (r.suggestedTitle || "").trim();
        setSuggestions(list);
        setSuggested(first);
        setNameInput(first);
      })
      .catch(() => {
        if (!cancelled) {
          setSuggested("");
          setSuggestions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [meeting?.autoTitled, meetingId]);

  async function saveName() {
    const t = nameInput.trim();
    if (!t) return;
    setSavingName(true);
    try {
      const co = companyInput.trim();
      const body: Record<string, unknown> = { title: t, autoTitled: false };
      if (needCompany && co) {
        body.account = { ...(meeting?.account ?? {}), name: co };
      }
      await api(`/meetings/${meetingId}`, { method: "PATCH", body });
      setMeeting((m) =>
        m
          ? {
              ...m,
              title: t,
              autoTitled: false,
              account: (body.account as Meeting["account"]) ?? m.account,
            }
          : m,
      );
      setNamingOpen(false);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSavingName(false);
    }
  }

  async function keepAutoName() {
    setNamingOpen(false);
    // Clear the flag so we don't prompt again on later visits.
    try {
      await summaryApi.renameMeeting(meetingId, meeting?.title ?? "");
    } catch {}
    setMeeting((m) => (m ? { ...m, autoTitled: false } : m));
  }

  const nameModal = namingOpen ? (
    <NameMeetingModal
      suggested={suggested}
      suggestions={suggestions}
      value={nameInput}
      onChange={setNameInput}
      saving={savingName}
      onSave={saveName}
      onKeep={keepAutoName}
      showCompany={needCompany}
      companyValue={companyInput}
      onCompanyChange={setCompanyInput}
      vofConsent={vofConsent}
      onVofConsentChange={toggleVofConsent}
    />
  ) : null;

  const isSimulation = meeting?.meetingType === "simulation";
  const scenario =
    meeting?.simulationScenario ??
    (meeting?.simulationScenarioId ? getScenarioById(meeting.simulationScenarioId) : null);

  function triggerLoad() {
    // Tear down any previous retry loop before starting a fresh one.
    loadCancelRef.current?.();
    setSummary(null);
    setLoadError(false);
    let cancelled = false;
    let attempts = 0;
    let lastKickAt = 0;
    let reportedSlow = false;
    // After this many fast polls (~2 min) we surface the "taking a while" state —
    // but we KEEP retrying automatically underneath until the summary appears.
    const SOFT_FAIL_AFTER = 40;
    // Re-kick a fresh generation at most this often, so a background job that
    // died silently gets restarted (the read path self-heals too, but a job may
    // be mid-flight and just slow).
    const REKICK_EVERY_MS = 30_000;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tryFetch(): Promise<MeetingSummary | null> {
      try {
        return await api<MeetingSummary>(`/meetings/${meetingId}/summary-data?_t=${Date.now()}`);
      } catch {
        return null;
      }
    }

    async function kickSummarize() {
      lastKickAt = Date.now();
      try {
        await summaryApi.triggerSummarize(meetingId);
      } catch {
        /* best-effort — the poll + server self-heal will keep trying */
      }
    }

    // Backoff so an indefinitely-retrying tab stays cheap: fast for the first
    // ~2 min, then steady. Never returns "stop" — we retry until we get it.
    function delayFor(n: number): number {
      if (n < SOFT_FAIL_AFTER) return 3000; // first ~2 min
      if (n < 120) return 10_000; // next ~13 min
      return 30_000; // thereafter, indefinitely while the page is open
    }

    async function tick() {
      if (cancelled) return;
      const result = await tryFetch();
      if (cancelled) return;
      if (result) {
        setSummary(result);
        setLoadError(false);
        return; // success — stop retrying
      }

      // Missing. On the very first miss, and then periodically, (re)kick generation.
      if (attempts === 0 || Date.now() - lastKickAt > REKICK_EVERY_MS) {
        void kickSummarize();
      }
      attempts++;

      // Past the fast window: show the "taking longer than usual" state (which
      // exposes a manual Retry) but DO NOT stop — auto-retry continues.
      if (attempts >= SOFT_FAIL_AFTER && !reportedSlow) {
        reportedSlow = true;
        setLoadError(true);
        // Surface to the User-bugs report once (404s + 202 are otherwise invisible
        // to the API telemetry). We keep auto-retrying regardless.
        void reportClientError({
          kind: "api",
          message: `Summary slow/not generated for meeting ${meetingId}; still auto-retrying`,
          extra: { meetingId, attempts: String(attempts) },
        });
      }

      timer = setTimeout(tick, delayFor(attempts));
    }

    const cancel = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    loadCancelRef.current = cancel;
    void tick();
    return cancel;
  }

  useEffect(() => {
    triggerLoad();
    return () => loadCancelRef.current?.();
  }, [meetingId]);

  async function exportPdf() {
    try {
      const blob = await summaryApi.exportPdf(meetingId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (summary?.meeting.client ?? "meeting").replace(/\W+/g, "-").toLowerCase();
      a.download = `summary-${slug}-${meetingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    }
  }

  // Export transcript + summary as Markdown to the user's Google Drive. If they haven't
  // connected Drive yet the API returns 409 → run the one-time consent popup, store the
  // token, and retry once. On success we open the file, toast a link, AND keep a
  // persistent "View in Drive" link on the page (driveExport) so it's easy to find.
  async function exportToDrive() {
    setDriveBusy(true);
    try {
      let res: Awaited<ReturnType<typeof summaryApi.exportToDrive>>;
      try {
        res = await summaryApi.exportToDrive(meetingId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && err.code === "drive-not-connected") {
          const code = await getDriveAuthCode();
          await driveApi.connect(code);
          res = await summaryApi.exportToDrive(meetingId);
        } else {
          throw err;
        }
      }
      setDriveExport({ url: res.url, fileName: res.fileName });
      // Open the created file immediately so the user lands on it in Drive, and keep a
      // persistent link on the page (below) in case the pop-up is blocked.
      window.open(res.url, "_blank", "noopener,noreferrer");
      toast.push({
        tone: "success",
        durationMs: 12_000,
        message: (
          <span>
            Saved “{res.fileName}” to Drive.{" "}
            <a href={res.url} target="_blank" rel="noopener noreferrer">
              Open file
            </a>
          </span>
        ),
      });
    } catch (err) {
      toast.push({
        tone: "error",
        message: `Export to Drive failed: ${(err as Error).message}`,
      });
    } finally {
      setDriveBusy(false);
    }
  }

  async function loadVersions() {
    try {
      setVersions(await summaryApi.listVersions(meetingId));
    } catch {
      /* non-blocking */
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once summary exists
  useEffect(() => {
    if (summary) void loadVersions();
  }, [meetingId, Boolean(summary)]);

  async function rerunSummary(technical = false) {
    if (
      !window.confirm(
        technical
          ? "Re-analyze the meeting with a technical deep dive (architecture, components, integrations, technical risks)? The current version is saved so you can restore it."
          : "Re-analyze the meeting and generate a new summary? The current version is saved so you can restore it.",
      )
    )
      return;
    setRerunning(true);
    try {
      const fresh = await summaryApi.regenerateSummary(meetingId, { technical });
      setSummary(fresh);
      await loadVersions();
      toast.push({
        tone: "success",
        message: technical ? "Technical deep dive ready" : "Summary re-analyzed",
      });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setRerunning(false);
    }
  }

  // Preview an older version in place — reads its stored payload, does NOT overwrite
  // `latest` and does NOT re-analyze. The user can then Restore it or go Back to latest.
  async function viewVersion(v: Omit<SummaryVersion, "summary">) {
    setSwitchingVersion(true);
    try {
      const payload = await summaryApi.getVersion(meetingId, v.id);
      setLatestBackup((prev) => prev ?? summary);
      setSummary(payload);
      setPreviewVersion({ id: v.id, label: v.label });
      setVersionsOpen(false);
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setSwitchingVersion(false);
    }
  }

  function exitPreview() {
    if (latestBackup) setSummary(latestBackup);
    setLatestBackup(null);
    setPreviewVersion(null);
  }

  async function restoreVersion(versionId: string) {
    setRerunning(true);
    try {
      const restored = await summaryApi.restoreVersion(meetingId, versionId);
      setSummary(restored);
      setLatestBackup(null);
      setPreviewVersion(null);
      await loadVersions();
      setVersionsOpen(false);
      toast.push({ tone: "info", message: "Restored previous version" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setRerunning(false);
    }
  }

  if (!summary) {
    return (
      <>
        {nameModal}
        <SummaryLoadingOverlay error={loadError} onRetry={triggerLoad} />
      </>
    );
  }

  return (
    <div className="summary">
      {nameModal}
      {rerunning && (
        <div className="rerun-overlay">
          <div className="rerun-card">
            <div className="rerun-dots">
              <span className="dot dot-pulse" style={{ background: "var(--gc-blue)" }} />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--gc-blue)", animationDelay: "160ms" }}
              />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--gc-blue)", animationDelay: "320ms" }}
              />
            </div>
            <div className="rerun-title">Re-analyzing the meeting…</div>
            <div className="rerun-sub">
              SellSynq is reading the transcript and writing a fresh summary. This takes a few seconds.
            </div>
          </div>
        </div>
      )}
      {switchingVersion && (
        <div className="rerun-overlay">
          <div className="rerun-card">
            <div className="rerun-dots">
              <span className="dot dot-pulse" style={{ background: "var(--gc-blue)" }} />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--gc-blue)", animationDelay: "160ms" }}
              />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--gc-blue)", animationDelay: "320ms" }}
              />
            </div>
            <div className="rerun-title">Loading previous version…</div>
            <div className="rerun-sub">Showing a saved version — nothing is re-analyzed.</div>
          </div>
        </div>
      )}
      {previewVersion && (
        <div className="version-preview-banner">
          <span>
            Viewing a previous version: <strong>{previewVersion.label}</strong> · read-only
          </span>
          <div className="version-preview-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => restoreVersion(previewVersion.id)}
              disabled={rerunning}
            >
              <Check size={14} /> Restore this version
            </button>
            <button type="button" className="ghost-btn" onClick={exitPreview} disabled={rerunning}>
              <Chev size={14} /> Back to latest
            </button>
          </div>
        </div>
      )}
      <header className="topbar">
        <div className="topbar-left">
          <button
            type="button"
            className="icon-btn"
            onClick={() => nav({ to: "/dashboard" })}
            aria-label="Back"
          >
            <Chev size={18} style={{ transform: "rotate(180deg)" }} />
          </button>
          <Link to="/dashboard" className="brand brand-link">
            <img src="/supercloud-mark.svg" alt="SellSynq" width={28} height={28} />
            <div className="brand-text">
              <div className="brand-name">Meeting summary</div>
              <div className="brand-sub">
                {summary.meeting.client} · {new Date(summary.meeting.date).toLocaleDateString()} ·{" "}
                {summary.meeting.duration}
                {summary.meeting.endedAt
                  ? ` · Finished ${new Date(summary.meeting.endedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : ""}
              </div>
            </div>
          </Link>
        </div>
        <div className="topbar-center">
          <div className="seg seg-tabs">
            <button
              type="button"
              className={tab === "internal" ? "on" : ""}
              onClick={() => setTab("internal")}
            >
              <Brain size={14} /> Internal summary
            </button>
            <button
              type="button"
              className={tab === "client" ? "on" : ""}
              onClick={() => setTab("client")}
            >
              <Send size={14} /> Client email
            </button>
            <button
              type="button"
              className={tab === "transcript" ? "on" : ""}
              onClick={() => setTab("transcript")}
            >
              <Notebook size={14} /> Full transcript
            </button>
            <button
              type="button"
              className={tab === "plugins" ? "on" : ""}
              onClick={() => setTab("plugins")}
            >
              <Bolt size={14} /> Plugins
            </button>
          </div>
        </div>
        <div className="topbar-right">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => rerunSummary(false)}
            disabled={rerunning}
            title="Re-analyze the meeting and generate a fresh summary"
          >
            <Spark size={14} /> {rerunning ? "Re-analyzing…" : "Re-run summary"}
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => rerunSummary(true)}
            disabled={rerunning}
            title="Re-analyze with a deeper technical focus (architecture, components, integrations, technical risks)"
          >
            <Brain size={14} /> Technical deep dive
          </button>
          {versions.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setVersionsOpen((v) => !v)}
                title="Restore a previous summary version"
              >
                <Chev size={14} /> Versions ({versions.length})
              </button>
              {versionsOpen && (
                <div className="sum-versions-menu">
                  <div className="sum-versions-head">Previous versions</div>
                  {versions.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`sum-versions-item${previewVersion?.id === v.id ? " on" : ""}`}
                      onClick={() => viewVersion(v)}
                      disabled={rerunning || switchingVersion}
                      title="View this version (does not overwrite the current summary)"
                    >
                      <span>{v.label}</span>
                      <span className="sum-versions-date">
                        {new Date(v.savedAt).toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="ghost-btn"
            title="Reopen this meeting and continue from where it stopped"
            onClick={async () => {
              try {
                await summaryApi.resumeMeeting(meetingId);
                nav({ to: "/meetings/$id/live", params: { id: meetingId } });
              } catch (err) {
                toast.push({ tone: "error", message: (err as Error).message });
              }
            }}
          >
            <Spark size={14} /> Continue meeting
          </button>
          <button type="button" className="ghost-btn" onClick={exportPdf}>
            <Copy size={14} /> Export PDF
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={exportToDrive}
            disabled={driveBusy}
            title="Save this meeting's transcript + summary as a Markdown file in your Google Drive"
          >
            <Doc size={14} /> {driveBusy ? "Exporting…" : "Export to Drive"}
          </button>
          {driveExport && (
            <a
              className="drive-export-link"
              href={driveExport.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${driveExport.fileName} in Google Drive`}
            >
              <Check size={14} /> View in Drive
            </a>
          )}
          <button type="button" className="pill-btn" onClick={() => setShareOpen(true)}>
            <Share size={14} /> Share
          </button>
        </div>
      </header>

      {meeting && meeting.account?.isInternal !== true && (
        <div
          style={{
            margin: "10px 16px 0",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: meeting.participantsNotified
              ? "rgba(52,168,83,.10)"
              : "rgba(251,188,4,.12)",
            border: meeting.participantsNotified
              ? "1px solid rgba(52,168,83,.5)"
              : "1px solid rgba(251,188,4,.6)",
            color: meeting.participantsNotified ? "var(--gc-green, #137333)" : "#8a6100",
          }}
        >
          {meeting.participantsNotified
            ? "✓ You confirmed participants were notified this meeting would be transcribed."
            : "⚠️ Not confirmed: there's no record that participants were told this meeting would be transcribed."}
        </div>
      )}

      <div className="sum-end-banner">
        <div className="sum-end-icon">
          <Check size={18} />
        </div>
        <div>
          <div className="sum-end-title">
            Meeting ended · summaries generated in{" "}
            {((summary.generationLatencyMs ?? 0) / 1000).toFixed(1)}s
          </div>
          <div className="sum-end-sub">
            Audio discarded per privacy policy. Transcript and summary stored in your workspace.
          </div>
        </div>
        <div className="sum-end-actions">
          <button type="button" className="ghost-btn">
            Undo end
          </button>
        </div>
      </div>

      <div
        style={{
          margin: "0 0 14px",
          padding: "10px 14px",
          borderRadius: 10,
          background: "var(--gc-blue-50, #e8f0fe)",
          border: "1px solid var(--gc-blue, #1a73e8)",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 6 }}>
          ⭐ How was SellSynq for this meeting?
        </div>
        <FeedbackStars block />
      </div>

      {/* Labels + "view in language" — placed right under the SellSynq review. */}
      {(meeting || tab === "internal" || tab === "client") && (
        <div
          style={{
            margin: "0 0 14px",
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid var(--border-soft)",
            background: "var(--surface)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {meeting && (
            <MeetingLabels
              labels={meeting.labels ?? []}
              readOnly={!canEditLabels}
              suggest={canEditLabels ? () => summaryApi.suggestLabels(meetingId) : undefined}
              onChange={saveLabels}
            />
          )}
          {(tab === "internal" || tab === "client") &&
            (() => {
              const translated =
                displayLang !== "en" && !!translation && translation.lang === displayLang;
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: "var(--text-3)" }}>View in:</span>
                  <select
                    className="setup-input"
                    style={{ width: "auto", padding: "4px 8px", fontSize: 13 }}
                    value={displayLang}
                    disabled={translating}
                    onChange={(e) => void viewInLang(e.target.value)}
                  >
                    <option value="en">English (original)</option>
                    {LANGUAGES.filter((l) => l.code !== "en").map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.labelEn}
                      </option>
                    ))}
                  </select>
                  {translating && <span style={{ color: "var(--text-4)" }}>Translating…</span>}
                  {translated && (
                    <span style={{ color: "var(--text-4)", fontSize: 12 }}>
                      Viewing in {langMeta(displayLang).labelEn} — switch to English to edit.
                    </span>
                  )}
                </div>
              );
            })()}
        </div>
      )}

      {isSimulation && summary && (
        <SimScoreBanner
          score={summary.internal.score}
          health={summary.internal.health}
          scenarioTitle={scenario?.title}
          difficulty={scenario?.difficulty}
        />
      )}

      <SuggestedQuestionsBanner compact />

      <div className="sum-body">
        {(() => {
          const translated =
            displayLang !== "en" && translation && translation.lang === displayLang;
          const viewSummary = translated ? applyTranslation(summary, translation) : summary;
          const langReadOnly = !!previewVersion || !!translated;
          return (
            <>
              {tab === "internal" && (
                <InternalSummary
                  summary={viewSummary}
                  onSummaryChange={setSummary}
                  readOnly={langReadOnly}
                />
              )}
              {tab === "client" && (
                <ClientEmail
                  summary={viewSummary}
                  onSummaryChange={setSummary}
                  readOnly={langReadOnly}
                />
              )}
            </>
          );
        })()}
        {tab === "transcript" && (
          <>
            <FullTranscript meetingId={meetingId} />
            <TranscriptChat meetingId={meetingId} />
          </>
        )}
        {tab === "plugins" && (
          <SummaryPluginsPanel meetingId={meetingId} summary={summary} meeting={meeting} />
        )}
      </div>

      {shareOpen && <ShareModal summary={summary} onClose={() => setShareOpen(false)} />}
    </div>
  );
}

function SimScoreBanner({
  score,
  health,
  scenarioTitle,
  difficulty,
}: {
  score: number;
  health: string;
  scenarioTitle?: string;
  difficulty?: string;
}) {
  const color =
    score >= 75
      ? "var(--gc-green, #1e8e3e)"
      : score >= 50
        ? "var(--gc-yellow, #b06000)"
        : "var(--gc-red, #d93025)";
  const bg =
    score >= 75
      ? "var(--gc-green-50, #e6f4ea)"
      : score >= 50
        ? "var(--gc-yellow-50, #fff7e0)"
        : "var(--gc-red-50, #fce8e6)";
  const rating = score >= 75 ? "High" : score >= 50 ? "Medium" : "Low";

  return (
    <div className="sim-score-banner" style={{ background: bg }}>
      <div className="sim-score-number" style={{ color }}>
        {rating}
      </div>
      <div className="sim-score-details">
        <div className="sim-score-label">Simulation Rating</div>
        {scenarioTitle && (
          <div className="sim-score-scenario">
            {scenarioTitle}
            {difficulty && <span className="sim-score-diff"> · {difficulty}</span>}
          </div>
        )}
        <div className="sim-score-health">
          Deal health: <strong>{health}</strong>
        </div>
      </div>
    </div>
  );
}

function NameMeetingModal({
  suggested,
  suggestions = [],
  value,
  onChange,
  saving,
  onSave,
  onKeep,
  showCompany,
  companyValue,
  onCompanyChange,
  vofConsent,
  onVofConsentChange,
}: {
  suggested: string | null;
  suggestions?: string[];
  value: string;
  onChange: (v: string) => void;
  saving: boolean;
  onSave: () => void;
  onKeep: () => void;
  showCompany?: boolean;
  companyValue?: string;
  onCompanyChange?: (v: string) => void;
  vofConsent: boolean;
  onVofConsentChange: (v: boolean) => void;
}) {
  const loading = suggested === null;
  return (
    <div className="modal-back" style={{ zIndex: 1000 }}>
      <div
        className="modal"
        style={{ maxWidth: 560, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <h3 className="modal-title">Name this meeting</h3>
            <p className="modal-sub">
              This was a quick meeting without a name. Pick one of the suggestions below (from the
              shared screen and the conversation) or type your own.
            </p>
          </div>
        </div>

        <div className="modal-body" style={{ overflow: "auto", flex: 1 }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 2px",
                color: "var(--text-3)",
                fontSize: 13,
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid var(--border)",
                  borderTopColor: "var(--gc-blue, #1a73e8)",
                  display: "inline-block",
                  animation: "nameSpin 0.8s linear infinite",
                }}
              />
              Finding a name from your meeting…
              <style>{`@keyframes nameSpin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <>
              {showCompany && (
                <div style={{ marginBottom: 12 }}>
                  <label
                    htmlFor="company-name"
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-3)",
                      marginBottom: 6,
                    }}
                  >
                    Company name
                  </label>
                  <input
                    id="company-name"
                    type="text"
                    value={companyValue ?? ""}
                    onChange={(e) => onCompanyChange?.(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--border)",
                      fontSize: 14,
                      color: "var(--text-1)",
                      background: "var(--surface)",
                    }}
                  />
                  <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 6 }}>
                    You skipped the company at the start — add it now (optional).
                  </div>
                </div>
              )}
              {suggestions.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-3)",
                      marginBottom: 6,
                    }}
                  >
                    Suggested names — pick one or type your own
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {suggestions.map((s) => {
                      const selected = value.trim() === s;
                      return (
                        <button
                          type="button"
                          key={s}
                          onClick={() => onChange(s)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            textAlign: "left",
                            padding: "9px 12px",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontSize: 14,
                            color: "var(--text-1)",
                            background: selected ? "var(--gc-blue-50, #e8f0fe)" : "var(--surface)",
                            border: `1px solid ${selected ? "var(--gc-blue, #1a73e8)" : "var(--border)"}`,
                          }}
                        >
                          <span
                            aria-hidden
                            style={{
                              flex: "0 0 auto",
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: `2px solid ${selected ? "var(--gc-blue, #1a73e8)" : "var(--border)"}`,
                              background: selected
                                ? "radial-gradient(circle, var(--gc-blue, #1a73e8) 0 4px, transparent 5px)"
                                : "transparent",
                            }}
                          />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <label
                htmlFor="meeting-name"
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text-3)",
                  marginBottom: 6,
                }}
              >
                {suggestions.length > 0 ? "Or type your own" : "Meeting name"}
              </label>
              <input
                id="meeting-name"
                type="text"
                value={value}
                autoFocus={suggestions.length === 0}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && value.trim() && !saving) onSave();
                }}
                placeholder="e.g. Acme Cloud Migration Kickoff"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  fontSize: 14,
                  color: "var(--text-1)",
                  background: "var(--surface)",
                }}
              />
              {!suggested && (
                <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 8 }}>
                  We couldn't infer a name automatically — type one above.
                </div>
              )}
            </>
          )}

          <div
            style={{
              borderTop: "1px solid var(--border-soft)",
              margin: "16px 0 12px",
            }}
          />
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              cursor: "pointer",
              fontSize: 13,
              color: "var(--text-2)",
              lineHeight: 1.5,
            }}
          >
            <input
              type="checkbox"
              checked={vofConsent}
              onChange={(e) => onVofConsentChange(e.target.checked)}
              style={{
                width: 16,
                height: 16,
                marginTop: 1,
                flex: "0 0 auto",
                accentColor: "var(--gc-blue, #1a73e8)",
              }}
            />
            <span>
              Share bug or feature requests &amp; insights from this meeting with Google product
              managers to help improve Google products.
            </span>
          </label>
        </div>

        <div className="modal-foot">
          <button type="button" className="ghost-btn" onClick={onKeep} disabled={saving}>
            Keep auto name
          </button>
          <button
            type="button"
            className="pill-btn primary"
            onClick={onSave}
            disabled={saving || loading || !value.trim()}
          >
            {saving ? "Saving…" : "Use this name"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryLoadingOverlay({ error, onRetry }: { error?: boolean; onRetry?: () => void }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (error) return;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      const next = Math.min(90, 90 * (1 - Math.exp(-elapsed / 5)));
      setPct(Math.round(next));
    }, 200);
    return () => clearInterval(id);
  }, [error]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: 400,
        gap: 24,
        padding: 32,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          // Not a hard failure — we keep retrying — so stay on the "working"
          // pulse even in the slow state.
          background: "var(--primary)",
          boxShadow: "0 8px 24px rgba(99, 102, 241, 0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "summaryPulse 2s ease-in-out infinite",
        }}
      >
        <Brain size={32} style={{ color: "#fff" }} />
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text-1)" }}>
          {error ? "Still generating your summary…" : "Generating Meeting Summary"}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 6 }}>
          {error
            ? "This is taking longer than usual — SellSynq is retrying automatically and will show it as soon as it’s ready. You can also retry now."
            : "Analyzing meeting transcript..."}
        </div>
      </div>

      {error ? (
        <button
          type="button"
          className="pill-btn primary"
          onClick={onRetry}
          style={{ marginTop: 8 }}
        >
          Retry now
        </button>
      ) : (
        <div style={{ width: "100%", maxWidth: 320 }}>
          <div
            style={{
              height: 8,
              borderRadius: 4,
              background: "var(--surface-2, #e5e7eb)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 4,
                background: "var(--primary)",
                width: `${pct}%`,
                transition: "width 0.3s ease-out",
              }}
            />
          </div>
          <div
            style={{
              textAlign: "center",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--text-2)",
              marginTop: 8,
            }}
          >
            {pct}%
          </div>
        </div>
      )}

      <style>{`
        @keyframes summaryPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}
